const SELECTORS = {
    emrnPdpQuote: '[data-emrn-pdp-quote]',
    emrnCardQuote: '.card-quoteBtn',
    emrnShoppingList: '[data-emrn-shopping-list]',
    finishQuoteLink: '[data-emrn-finish-quote-link], #myquote-entry > a',
    cartDrawerQuote: '#cartDrawerQuoteBtn',
    cartPageQuote: '.emrn-cart-summary .emrn-add-all-to-quote, [data-emrn-mobile-quote], .emrn-addAllQuoteBtn, .b2b-cart-to-quote',
    finishQuoteButtons: [
        '.emrn-finish-quote-button',
        '.finish-quote-button',
        'a[href*="quote"][class*="finish" i]',
        'button[class*="finish" i]',
        '.MuiSnackbar-root button',
    ].join(','),
    legacyQuoteControls: [
        '.b2b-add-to-quote',
        '.b2b-add-to-no-purchasable-quote',
        '.b2b-add-to-list',
        '.b2b-add-all-to-quote',
        '.b2b-cart-to-quote',
        '.rfq-button-container',
        '.rfq-pdp-button-container',
        '.rfq-table-addAllQuoteBtn',
        '.rfq-pdp-button-container',
        'button.add-to-quote',
        'button.add-all-to-quote',
    ].join(','),
};

const QUOTE_STORAGE_BACKUP_KEY = 'emrnBuyerPortalQuoteSessionBackup';
const QUOTE_STORAGE_REQUEST_KEY = 'emrnBuyerPortalQuoteSessionRequest';
const QUOTE_STORAGE_RESPONSE_KEY = 'emrnBuyerPortalQuoteSessionResponse';
const QUOTE_STORAGE_PATTERNS = [
    /b2b/i,
    /buyer.?portal/i,
    /quote/i,
    /rfq/i,
];

function isQuoteStorageKey(key) {
    return QUOTE_STORAGE_PATTERNS.some((pattern) => pattern.test(key || ''));
}

function isEmptyStorageValue(value) {
    const text = String(value || '').trim();

    return !text || text === '{}' || text === '[]' || text === 'null' || text === 'undefined';
}

function hasCurrentQuoteSessionStorage() {
    try {
        for (let index = 0; index < window.sessionStorage.length; index++) {
            const key = window.sessionStorage.key(index);
            if (isQuoteStorageKey(key) && !isEmptyStorageValue(window.sessionStorage.getItem(key))) {
                return true;
            }
        }
    } catch (err) {
        return false;
    }

    return false;
}

function getQuoteSessionStorageValues() {
    const values = {};

    for (let index = 0; index < window.sessionStorage.length; index++) {
        const key = window.sessionStorage.key(index);
        const value = window.sessionStorage.getItem(key);

        if (isQuoteStorageKey(key) && !isEmptyStorageValue(value)) {
            values[key] = value;
        }
    }

    return values;
}

function writeQuoteSessionStorageBackup(values) {
    if (!values || !Object.keys(values).length) return false;

    window.localStorage.setItem(QUOTE_STORAGE_BACKUP_KEY, JSON.stringify({
        savedAt: Date.now(),
        values,
    }));

    return true;
}

function saveQuoteSessionStorageBackup() {
    try {
        return writeQuoteSessionStorageBackup(getQuoteSessionStorageValues());
    } catch (err) {
        // Storage can be blocked in private modes; Buyer Portal still works in-tab.
        return false;
    }
}

function restoreQuoteSessionStorageBackup() {
    try {
        if (hasCurrentQuoteSessionStorage()) return;

        const rawBackup = window.localStorage.getItem(QUOTE_STORAGE_BACKUP_KEY);
        if (!rawBackup) return;

        const backup = JSON.parse(rawBackup);
        const values = backup && backup.values;
        if (!values || typeof values !== 'object') return;

        Object.keys(values).forEach((key) => {
            if (!isQuoteStorageKey(key) || isEmptyStorageValue(values[key])) return;

            const currentValue = window.sessionStorage.getItem(key);
            if (isEmptyStorageValue(currentValue)) {
                window.sessionStorage.setItem(key, values[key]);
            }
        });
    } catch (err) {
        // Ignore malformed or unavailable storage and let Buyer Portal initialize normally.
    }
}

function requestQuoteSessionFromOtherTabs() {
    try {
        if (hasCurrentQuoteSessionStorage()) return;

        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem(QUOTE_STORAGE_REQUEST_KEY, JSON.stringify({
            requestId,
            requestedAt: Date.now(),
        }));

        setTimeout(restoreQuoteSessionStorageBackup, 150);
        setTimeout(restoreQuoteSessionStorageBackup, 500);
        setTimeout(restoreQuoteSessionStorageBackup, 1200);
    } catch (err) {
        // Ignore unavailable storage and let Buyer Portal initialize normally.
    }
}

function handleQuoteStorageEvent(event) {
    if (!event || !event.key) return;

    if (event.key === QUOTE_STORAGE_REQUEST_KEY) {
        if (!hasCurrentQuoteSessionStorage()) return;

        try {
            const values = getQuoteSessionStorageValues();
            if (!writeQuoteSessionStorageBackup(values)) return;

            window.localStorage.setItem(QUOTE_STORAGE_RESPONSE_KEY, JSON.stringify({
                respondedAt: Date.now(),
                values,
            }));
        } catch (err) {
            // Ignore malformed storage events.
        }
        return;
    }

    if (event.key === QUOTE_STORAGE_RESPONSE_KEY || event.key === QUOTE_STORAGE_BACKUP_KEY) {
        restoreQuoteSessionStorageBackup();
    }
}

function installQuoteStorageMirror() {
    try {
        if (window.__emrnBuyerPortalQuoteStorageMirror) return;
        window.__emrnBuyerPortalQuoteStorageMirror = true;

        restoreQuoteSessionStorageBackup();
        requestQuoteSessionFromOtherTabs();

        const originalSetItem = window.sessionStorage.setItem.bind(window.sessionStorage);
        const originalRemoveItem = window.sessionStorage.removeItem.bind(window.sessionStorage);
        const originalClear = window.sessionStorage.clear.bind(window.sessionStorage);

        window.sessionStorage.setItem = (key, value) => {
            originalSetItem(key, value);
            if (isQuoteStorageKey(key) && !isEmptyStorageValue(value)) saveQuoteSessionStorageBackup();
        };

        window.sessionStorage.removeItem = (key) => {
            originalRemoveItem(key);
            if (isQuoteStorageKey(key)) setTimeout(saveQuoteSessionStorageBackup, 0);
        };

        window.sessionStorage.clear = () => {
            originalClear();
            setTimeout(saveQuoteSessionStorageBackup, 0);
        };

        window.addEventListener('storage', handleQuoteStorageEvent);
        window.addEventListener('pagehide', saveQuoteSessionStorageBackup);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') saveQuoteSessionStorageBackup();
        });
    } catch (err) {
        // Some browsers disallow patching Storage methods; the one-time restore above is enough.
        restoreQuoteSessionStorageBackup();
    }
}

function waitForB2BUtils(timeout = 7000) {
    const started = Date.now();

    return new Promise((resolve, reject) => {
        const timer = setInterval(() => {
            const utils = window.b2b && window.b2b.utils;
            if (utils && utils.quote) {
                clearInterval(timer);
                resolve(utils);
                return;
            }

            if (Date.now() - started >= timeout) {
                clearInterval(timer);
                reject(new Error('Buyer Portal utilities were not available yet.'));
            }
        }, 150);
    });
}

function callFirstAvailable(target, names, args = []) {
    if (!target) return Promise.reject(new Error('Buyer Portal utility group was not available.'));

    const name = names.find((methodName) => typeof target[methodName] === 'function');
    if (!name) {
        return Promise.reject(new Error(`Buyer Portal utility was not available: ${names.join(', ')}`));
    }

    return Promise.resolve(target[name](...args));
}

function addQuoteItemWithAvailableMethod(utils, item, fromPage) {
    const quote = utils && utils.quote;

    if (!item) {
        return Promise.reject(new Error('Quote item was not available.'));
    }

    if (quote && typeof quote.addProducts === 'function') {
        return Promise.resolve(quote.addProducts([item]));
    }

    if (quote && typeof quote.addProduct === 'function') {
        return Promise.resolve(quote.addProduct(item));
    }

    if (fromPage) {
        return callFirstAvailable(quote, ['addProductFromPage', 'addProductsFromPage'], [item]);
    }

    return Promise.reject(new Error('Buyer Portal quote add utility was not available.'));
}

function getQty(root = document) {
    const input = root.querySelector('input[name="qty[]"], .form-input--incrementTotal');
    const qty = parseInt(input && input.value, 10);
    return Number.isNaN(qty) || qty < 1 ? 1 : qty;
}

function getCleanSku(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
}

function getSkuFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        return getCleanSku(params.get('sku') || params.get('SKU') || params.get('variantSku') || '');
    } catch (err) {
        return '';
    }
}

function getEventElement(event) {
    const target = event && event.target;

    if (!target) return null;
    if (target.nodeType === 1) return target;

    return target.parentElement || null;
}

function closestFromEvent(event, selector) {
    const target = getEventElement(event);

    if (target && target.closest) {
        const match = target.closest(selector);
        if (match) return match;
    }

    if (!event || !event.composedPath) return null;

    return event.composedPath().find((node) => (
        node &&
        node.nodeType === 1 &&
        node.matches &&
        node.matches(selector)
    )) || null;
}

function getSkuFromNode(node) {
    if (!node) return '';

    return getCleanSku(
        node.dataset.productSku ||
        node.dataset.sku ||
        node.getAttribute('data-product-sku') ||
        node.getAttribute('data-sku') ||
        node.textContent
    );
}

function normalizeQuoteItem(quantity, productEntityId, sku, variantId = null, selectedOptions = []) {
    const item = {
        quantity,
        productEntityId,
        sku,
    };

    if (variantId) {
        item.variantEntityId = variantId;
    }

    if (selectedOptions.length) {
        item.selectedOptions = selectedOptions;
    }

    return item;
}

function getSelectedOptions(form) {
    if (!form) return [];

    const optionsById = new Map();
    const optionNamePattern = /^attribute\[(\d+)\]$/;
    const fields = form.querySelectorAll('[name^="attribute["]');

    fields.forEach((field) => {
        const match = optionNamePattern.exec(field.name || '');
        if (!match) return;

        const optionEntityId = parseInt(match[1], 10);
        if (!optionEntityId) return;

        const tagName = (field.tagName || '').toLowerCase();
        const type = (field.type || '').toLowerCase();

        if ((type === 'radio' || type === 'checkbox') && !field.checked) return;
        if (tagName === 'select' && (!field.value || field.value === '')) return;
        if (!field.value || Number.isNaN(Number(field.value))) return;

        const optionValueEntityId = parseInt(field.value, 10);
        if (!optionValueEntityId) return;

        optionsById.set(optionEntityId, {
            optionEntityId,
            optionValueEntityId,
            valueEntityId: optionValueEntityId,
        });
    });

    return Array.from(optionsById.values());
}

function hasUnselectedRequiredOptions(form) {
    if (!form) return false;

    const groups = new Map();
    const optionNamePattern = /^attribute\[(\d+)\]$/;
    const fields = form.querySelectorAll('[name^="attribute["]');

    fields.forEach((field) => {
        const match = optionNamePattern.exec(field.name || '');
        if (!match) return;

        const optionId = match[1];
        if (!groups.has(optionId)) groups.set(optionId, []);
        groups.get(optionId).push(field);
    });

    return Array.from(groups.values()).some((group) => {
        const required = group.some((field) => (
            field.required ||
            field.getAttribute('aria-required') === 'true' ||
            (field.closest('.form-field') && /required/i.test(field.closest('.form-field').textContent || ''))
        ));

        if (!required) return false;

        return !group.some((field) => {
            const tagName = (field.tagName || '').toLowerCase();
            const type = (field.type || '').toLowerCase();

            if (type === 'radio' || type === 'checkbox') return field.checked;
            if (tagName === 'select') return !!field.value;

            return !!field.value;
        });
    });
}

function getStoredVariantId(productView, form) {
    const variantInput = form && form.querySelector('input[name="emrn_variant_id"], input[name="variation_id"], input[name="variant_id"]');
    const params = new URLSearchParams(window.location.search);
    const rawVariantId = (
        params.get('variant_id') ||
        params.get('variantId') ||
        params.get('variant') ||
        productView.getAttribute('data-emrn-current-variant-id') ||
        (variantInput && variantInput.value) ||
        ''
    );
    const variantId = parseInt(rawVariantId, 10);

    return Number.isNaN(variantId) || variantId < 1 ? null : variantId;
}

function getDatasetVariantId(node) {
    if (!node || !node.dataset) return null;

    const rawVariantId =
        node.dataset.variantId ||
        node.dataset.variantEntityId ||
        node.dataset.productVariantId ||
        node.dataset.productVariantEntityId ||
        node.dataset.emrnVariantId ||
        '';
    const variantId = parseInt(rawVariantId, 10);

    return Number.isNaN(variantId) || variantId < 1 ? null : variantId;
}

function getPdpQuoteItem(btn) {
    const productView = btn.closest('.productView') || document;
    const form = productView.querySelector('form[action*="cart.php"]') || productView.querySelector('form');
    const productIdInput = form && form.querySelector('input[name="product_id"]');
    const productEntityId = parseInt(productIdInput && productIdInput.value, 10);
    const skuEl = productView.querySelector('[data-product-sku]');
    const urlSku = getSkuFromUrl();
    const visibleSku = getCleanSku(skuEl && skuEl.textContent);
    const rawSku = getCleanSku(urlSku || visibleSku);
    const sku = /select options/i.test(rawSku) ? '' : rawSku;
    const variantId = getStoredVariantId(productView, form);
    const selectedOptions = getSelectedOptions(form);

    if (!urlSku && /select options/i.test(visibleSku) && hasUnselectedRequiredOptions(form)) {
        return { missingOptions: true };
    }

    if (!productEntityId || (!sku && !variantId && !selectedOptions.length)) return null;

    return normalizeQuoteItem(getQty(productView), productEntityId, sku, variantId, selectedOptions);
}

function getCardQuoteItem(btn) {
    const card = btn.closest('[data-entity-id], [data-product-id], .card, .fs-results-product-card, [role="group"]') || btn;
    const productLink = card.querySelector('a[href*="/"]:not([href="#add-to-quote"]):not([href="#"])');
    const cardAddToCart = card.querySelector('[data-button-type="add-cart"][data-product-id], .card-quick-add-button[data-product-id], [data-product-id]');
    const productEntityId = parseInt(
        btn.dataset.productId ||
        card.dataset.productId ||
        card.dataset.entityId ||
        (cardAddToCart && cardAddToCart.dataset.productId) ||
        (productLink && productLink.dataset.productId),
        10
    );
    const skuNode = card.querySelector('[data-product-sku], [data-sku], .card-sku, .product-sku, [class*="sku"]');
    const sku = getCleanSku(btn.dataset.productSku || card.dataset.productSku || getSkuFromNode(skuNode));
    const variantId = getDatasetVariantId(btn) || getDatasetVariantId(card);

    if (!productEntityId || !sku) return null;

    return normalizeQuoteItem(getQty(card), productEntityId, sku, variantId);
}

function getQuoteCount(data) {
    if (!data || typeof data !== 'object') return 0;

    const itemArrays = [
        data.products,
        data.productList,
        data.items,
        data.lineItems,
        data.quoteProducts,
        data.quoteProductList,
    ].filter(Array.isArray);

    if (!itemArrays.length) return 0;

    return itemArrays[0].reduce((total, item) => {
        const qty = parseInt(item.quantity || item.qty || 1, 10);
        return total + (Number.isNaN(qty) ? 1 : qty);
    }, 0);
}

function getHeaderQuoteCount() {
    let count = 0;

    document.querySelectorAll('[data-emrn-quote-count], .quote-quantity.countPill[data-role="header-quote-count"]').forEach((el) => {
        const raw = el.getAttribute('data-emrn-quote-count') || el.textContent || '';
        const match = String(raw).match(/\d+/);
        if (!match) return;

        const value = parseInt(match[0], 10);
        if (!Number.isNaN(value)) {
            count = Math.max(count, value);
        }
    });

    return count;
}

function getCurrentQuote(utils) {
    const quote = utils && utils.quote;

    if (!quote || typeof quote.getCurrent !== 'function') {
        return Promise.resolve(null);
    }

    return Promise.resolve(quote.getCurrent()).catch(() => null);
}

function waitForQuoteHydration(utils, timeout = 12000) {
    const quote = utils && utils.quote;

    if (!quote || typeof quote.getCurrent !== 'function') {
        return Promise.resolve();
    }

    const started = Date.now();
    const headerCount = getHeaderQuoteCount();
    let lastCount = null;
    let stableSince = 0;

    return new Promise((resolve) => {
        let done = false;
        let timer = null;

        const finish = () => {
            if (done) return;
            done = true;
            if (timer) clearInterval(timer);
            resolve();
        };

        const check = () => {
            getCurrentQuote(utils).then((currentQuote) => {
                const currentCount = getQuoteCount(currentQuote);

                if (currentCount !== lastCount) {
                    lastCount = currentCount;
                    stableSince = Date.now();
                }

                const isStable = stableSince && Date.now() - stableSince >= 900;
                const existingDraftLoaded = !headerCount || currentCount >= headerCount;
                const timedOut = Date.now() - started >= timeout;

                if ((isStable && existingDraftLoaded) || timedOut) {
                    finish();
                }
            });
        };

        timer = setInterval(check, 250);
        check();
    });
}

function updateQuoteCount(count) {
    const displayCount = parseInt(count, 10) || 0;

    document.querySelectorAll('[data-emrn-quote-count], .quote-quantity.countPill[data-role="header-quote-count"]').forEach((el) => {
        if (!displayCount) {
            el.textContent = '';
            el.classList.remove('is-visible');
            el.classList.remove('countPill--positive');
            el.style.setProperty('display', 'none');
            return;
        }

        el.textContent = String(displayCount);
        el.setAttribute('data-emrn-quote-count', String(displayCount));
        el.classList.add('is-visible');
        el.classList.add('countPill--positive');
        el.style.setProperty('display', 'inline-flex', 'important');
    });
}

function setButtonState(btn, state) {
    if (!btn) return;

    if (!btn.dataset.originalText) {
        btn.dataset.originalText = (btn.textContent || btn.value || '').trim();
    }

    btn.classList.remove('is-loading', 'is-success', 'emrn-atc-loading', 'emrn-atc-success');

    const text = {
        loading: 'Adding to quote...',
        success: 'Added to quote',
        reset: btn.dataset.originalText || 'Add to Quote',
    }[state];

    if (state === 'loading') btn.classList.add('is-loading', 'emrn-atc-loading');
    if (state === 'success') btn.classList.add('is-success', 'emrn-atc-success');

    if (text) {
        if (btn.tagName === 'INPUT') btn.value = text;
        else btn.textContent = text;
    }
}

function showQuoteToast(message) {
    let toast = document.getElementById('quoteInlineToast');

    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'quoteInlineToast';
        toast.className = 'emrn-quote-toast';
        document.body.appendChild(toast);
    }

    Object.assign(toast.style, {
        position: 'fixed',
        top: '92px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '2147483000',
        maxWidth: 'calc(100vw - 32px)',
        padding: '11px 16px',
        borderRadius: '999px',
        border: '1px solid rgba(195, 77, 80, 0.25)',
        background: '#ffffff',
        color: '#3b363e',
        boxShadow: '0 12px 30px rgba(20, 24, 28, 0.18)',
        fontSize: '14px',
        fontWeight: '700',
        lineHeight: '1.3',
        textAlign: 'center',
        pointerEvents: 'none',
        opacity: '1',
        transition: 'opacity 180ms ease',
    });

    toast.textContent = message;
    toast.classList.add('is-visible');

    clearTimeout(toast.hideTimer);
    toast.hideTimer = setTimeout(() => {
        toast.style.opacity = '0';
        toast.classList.remove('is-visible');
    }, 3000);
}

function finishButton(btn, ok = true) {
    setButtonState(btn, ok ? 'success' : 'reset');
    setTimeout(() => setButtonState(btn, 'reset'), 2600);
}

function addItemToBuyerPortalQuote(btn, item, fromPage = false) {
    if (!item) {
        showQuoteToast('Please choose options before adding this item to a quote.');
        return Promise.reject(new Error('Missing product id or SKU for quote item.'));
    }

    setButtonState(btn, 'loading');

    restoreQuoteSessionStorageBackup();

    return waitForB2BUtils()
        .then((utils) => waitForQuoteHydration(utils).then(() => utils))
        .then((utils) => addQuoteItemWithAvailableMethod(utils, item, fromPage))
        .then(() => {
            saveQuoteSessionStorageBackup();
            finishButton(btn, true);
            refreshQuoteCount(true);
        })
        .catch((err) => {
            console.warn('EMRN Buyer Portal quote add failed:', err);
            finishButton(btn, false);
            showQuoteToast('Buyer Portal is still loading. Please try again in a moment.');
        });
}

function addItemToBuyerPortalShoppingList(btn, item) {
    const nativeButton = Array.from(document.querySelectorAll('.b2b-add-to-list button, button.b2b-add-to-list, .b2b-add-to-list')).find((node) => (
        node &&
        node !== btn &&
        !node.matches(SELECTORS.emrnShoppingList)
    ));

    if (!nativeButton) {
        showQuoteToast('Shopping lists are still loading. Please try again in a moment.');
        return Promise.reject(new Error('Native Buyer Portal shopping list button was not available.'));
    }

    if ('disabled' in nativeButton) nativeButton.disabled = false;
    nativeButton.removeAttribute('disabled');
    nativeButton.click();

    return Promise.resolve();
}

function addCartToBuyerPortalQuote(btn) {
    setButtonState(btn, 'loading');

    restoreQuoteSessionStorageBackup();

    return waitForB2BUtils()
        .then((utils) => callFirstAvailable(utils.quote, ['addProductsFromCart', 'addCartProducts', 'addAllFromCart']))
        .then(() => {
            saveQuoteSessionStorageBackup();
            finishButton(btn, true);
            refreshQuoteCount(true);
        })
        .catch((err) => {
            console.warn('EMRN Buyer Portal cart quote failed:', err);
            finishButton(btn, false);
            showQuoteToast('Buyer Portal is still loading. Please try again in a moment.');
        });
}

function wireQuoteClicks() {
    document.addEventListener('click', (event) => {
        const cartBtn = closestFromEvent(event, `${SELECTORS.cartDrawerQuote}, ${SELECTORS.cartPageQuote}`);
        if (cartBtn) {
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
            addCartToBuyerPortalQuote(cartBtn);
            return;
        }

        const pdpBtn = closestFromEvent(event, SELECTORS.emrnPdpQuote);
        if (pdpBtn) {
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
            const pdpItem = getPdpQuoteItem(pdpBtn);

            if (pdpItem && pdpItem.missingOptions) {
                showQuoteToast('Please pick options before adding this item to a quote.');
                return;
            }

            addItemToBuyerPortalQuote(pdpBtn, pdpItem, true);
            return;
        }

        const shoppingListBtn = closestFromEvent(event, SELECTORS.emrnShoppingList);
        if (shoppingListBtn) {
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
            const shoppingListItem = getPdpQuoteItem(shoppingListBtn);

            if (shoppingListItem && shoppingListItem.missingOptions) {
                showQuoteToast('Please pick options before adding this item to a shopping list.');
                return;
            }

            addItemToBuyerPortalShoppingList(shoppingListBtn, shoppingListItem);
            return;
        }

        const cardBtn = closestFromEvent(event, SELECTORS.emrnCardQuote);
        if (!cardBtn || (cardBtn.closest && cardBtn.closest('.productView'))) return;

        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();

        const item = getCardQuoteItem(cardBtn);
        if (!item) {
            showQuoteToast('Please choose options on the product page before adding this item to a quote.');
            return;
        }

        addItemToBuyerPortalQuote(cardBtn, item, false);
    }, true);
}

function getVisibleFinishQuoteButton() {
    return Array.from(document.querySelectorAll(SELECTORS.finishQuoteButtons)).find((btn) => {
        const text = (btn.textContent || btn.value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const rect = btn.getBoundingClientRect();
        const style = window.getComputedStyle(btn);

        return (
            text.includes('finish quote') ||
            text.includes('open quote') ||
            text.includes('my quote')
        ) &&
            rect.width > 0 &&
            rect.height > 0 &&
            style.display !== 'none' &&
            style.visibility !== 'hidden';
    });
}

function openFinishQuote() {
    const finishButton = getVisibleFinishQuoteButton();

    if (finishButton) {
        finishButton.click();
        return true;
    }

    const utils = window.b2b && window.b2b.utils;
    if (!utils || !utils.openPage) return false;

    const routes = ['QUOTE', 'QUOTES', 'DRAFT_QUOTE', 'RFQ'];

    return routes.some((route) => {
        try {
            utils.openPage(route);
            return true;
        } catch (err) {
            return false;
        }
    });
}

function runAutoQuoteFromPdp() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quote') !== '1') return;

    const btn = document.querySelector(SELECTORS.emrnPdpQuote);
    if (!btn || btn.dataset.emrnPdpAutoPlayed) return;

    btn.dataset.emrnPdpAutoPlayed = '1';
    addItemToBuyerPortalQuote(btn, getPdpQuoteItem(btn), true).finally(() => {
        if (window.history && window.history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.delete('quote');
            window.history.replaceState({}, '', url.toString());
        }
    });
}

function runAutoQuoteFromCart() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('autoQuote')) return;

    const btn = document.querySelector(`${SELECTORS.cartPageQuote}, ${SELECTORS.cartDrawerQuote}`);
    const notice = document.createElement('div');

    notice.className = 'emrn-auto-quote-notice';
    notice.textContent = 'Creating your quote...';

    const container = document.querySelector('.page') || document.body;
    container.insertBefore(notice, container.firstChild);

    addCartToBuyerPortalQuote(btn).finally(() => {
        notice.textContent = 'Cart items were sent to your Buyer Portal quote.';

        if (window.history && window.history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.delete('autoQuote');
            window.history.replaceState({}, '', url.toString());
        }

        setTimeout(() => notice.remove(), 3000);
    });
}

function syncHeaderQuoteLink() {
    const candidates = document.querySelectorAll(`${SELECTORS.finishQuoteLink}, .account-dropdown a[href*="quote"], .account-dropdown a[href="#finish-quote"]`);
    const links = Array.from(candidates).filter((link) => {
        if (link.matches(`${SELECTORS.emrnCardQuote}, ${SELECTORS.emrnPdpQuote}`)) return false;

        const text = (link.textContent || '').toLowerCase();
        const href = (link.getAttribute('href') || '').toLowerCase();

        return text.includes('my quote') || text.includes('quote') || href.includes('quote');
    });

    links.forEach((link) => {
        if (link.dataset.emrnBuyerPortalLink) return;

        link.dataset.emrnBuyerPortalLink = '1';
        link.setAttribute('href', '#finish-quote');
        link.setAttribute('aria-label', 'Finish Quote');

        let count = link.querySelector('[data-role="header-quote-count"]') || link.querySelector('[data-emrn-quote-count]');
        if (!count) {
            count = document.createElement('span');
            link.appendChild(count);
        }

        if (!count.hasAttribute('data-emrn-quote-count')) {
            count.setAttribute('data-emrn-quote-count', '');
        }

        count.classList.add('emrn-quote-count-bubble');

        link.addEventListener('click', (event) => {
            event.preventDefault();

            waitForB2BUtils()
                .then((utils) => (utils.quote && utils.quote.getCurrent ? utils.quote.getCurrent() : null))
                .then((quote) => {
                    const liveCount = getQuoteCount(quote);
                    if (!liveCount) {
                        showQuoteToast('Add something to your quote to view My Quote.');
                        return;
                    }

                    updateQuoteCount(liveCount);
                    if (!openFinishQuote()) showQuoteToast('Finish quote is still loading. Please try again in a moment.');
                })
                .catch(() => {
                    showQuoteToast('Buyer Portal quote is still loading. Please try again in a moment.');
                });
        });
    });
}

function ensureShoppingListButton() {
    const quoteAction = document.querySelector('.emrn-pdp-quote-action');
    const productView = document.querySelector('.productView');
    if (!quoteAction || !productView || document.querySelector(SELECTORS.emrnShoppingList)) return;

    const action = document.createElement('div');
    action.className = 'form-action emrn-shopping-list-action';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button emrn-shopping-listBtn';
    btn.setAttribute('data-emrn-shopping-list', '');
    btn.textContent = 'Add to shopping list';

    action.appendChild(btn);
    quoteAction.insertAdjacentElement('afterend', action);
}

let quoteCountRefreshStarted = false;

function refreshQuoteCount(force = false) {
    if (quoteCountRefreshStarted && !force) return;
    quoteCountRefreshStarted = true;

    waitForB2BUtils(2500)
        .then((utils) => (utils.quote && utils.quote.getCurrent ? utils.quote.getCurrent() : null))
        .then((quote) => {
            saveQuoteSessionStorageBackup();
            updateQuoteCount(getQuoteCount(quote));
        })
        .catch(() => {});
}

function removeLegacyPdpQuoteNotices() {
    document.querySelectorAll('#pdpQuoteNotice, .emrn-pdp-quote-notice').forEach((notice) => notice.remove());
}

function tidyLegacyInjectedControls() {
    document.querySelectorAll(SELECTORS.legacyQuoteControls).forEach((node) => {
        const hasCustomCartQuote = Boolean(document.querySelector(`${SELECTORS.cartDrawerQuote}, .emrn-cart-summary .emrn-add-all-to-quote, [data-emrn-mobile-quote]`));
        const isLegacyCartQuote = node.matches('.b2b-cart-to-quote, .emrn-addAllQuoteBtn');

        if (node.matches('.b2b-add-to-list')) {
            node.classList.add('emrn-buyer-portal-native-quote');
            if ('disabled' in node) node.disabled = false;
            node.removeAttribute('disabled');
            node.style.setProperty('display', 'none', 'important');
            node.style.setProperty('visibility', 'hidden', 'important');
            return;
        }

        if (isLegacyCartQuote && !hasCustomCartQuote) {
            node.classList.add('emrn-buyer-portal-cart-quote');
            node.removeAttribute('aria-hidden');
            node.removeAttribute('tabindex');
            if ('disabled' in node) node.disabled = false;
            return;
        }

        if (
            node.matches(SELECTORS.emrnPdpQuote) ||
            node.matches(SELECTORS.cartDrawerQuote)
        ) return;

        node.classList.add('emrn-buyer-portal-native-quote');
        node.setAttribute('aria-hidden', 'true');
        node.setAttribute('tabindex', '-1');
        if ('disabled' in node) node.disabled = true;
        node.style.setProperty('display', 'none', 'important');
        node.style.setProperty('visibility', 'hidden', 'important');
        node.style.setProperty('position', 'absolute', 'important');
        node.style.setProperty('left', '-9999px', 'important');
    });
}

function observeLegacyControls() {
    tidyLegacyInjectedControls();
}

function runLimitedSetupRetries() {
    const delays = [500, 1500, 3000, 6000];

    delays.forEach((delay) => {
        setTimeout(() => {
            syncHeaderQuoteLink();
            ensureShoppingListButton();
            removeLegacyPdpQuoteNotices();
            tidyLegacyInjectedControls();
        }, delay);
    });
}

export default function emrnBuyerPortal() {
    installQuoteStorageMirror();
    wireQuoteClicks();
    syncHeaderQuoteLink();
    ensureShoppingListButton();
    refreshQuoteCount();
    removeLegacyPdpQuoteNotices();
    runAutoQuoteFromPdp();
    runAutoQuoteFromCart();
    observeLegacyControls();
    runLimitedSetupRetries();
}
